import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  styleConstants,
} from '@paywithextend/components'
import {
  base_colorContentBorder,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_regular,
  font_family_weight_semibold,
  font_md,
  font_sm,
} from 'ext-ui/style/typography'
import {
  sidenavCollapsedWidthPx,
  sidenavExpandedWidthPx,
} from 'src/components/SidenavWrapper/sidenav.constants'
import { css, CSSProp } from 'styled-components'

import {
  CONDENSED_HERO_HEIGHT,
  GLOBAL_HEADER_HEIGHT_PX,
} from '../cardDetailHeroConstants'

const px = (value: string): number => Number(value.replace('px', ''))

/**
 * How far the fixed bar must inset from the left so it clears the fixed sidenav,
 * matching how `TopBar` / `LayoutShell` position their own fixed chrome: the bar
 * is anchored `right: 0` and shrunk by the sidenav's reserved width — the full
 * expanded width when the nav is locked open, otherwise the collapsed rail. A
 * viewport-width bar (`left: 0`) would slide its leading content (avatar +
 * cardholder name) under the opaque sidenav and hide it; the full hero avoids
 * this only because it lives in the content column rather than being fixed.
 */
export const sidebarRightShift = (sideNavLockedOpen: boolean): string =>
  sideNavLockedOpen ? sidenavExpandedWidthPx : sidenavCollapsedWidthPx

/**
 * Mirror the `<Header>` band's horizontal padding at every breakpoint so the
 * compact bar's content lines up exactly with the full hero it replaces. The
 * `<Header>` band scrolls away, so the bar carries its own opaque background and
 * this padding rather than inheriting either. Built lazily inside `bar()` (not a
 * module-scope const) so importing this module never reads `@paywithextend`
 * theme tokens — only rendering the bar does, matching `<Header>`'s pattern.
 */
const horizontalPadding = (): CSSProp => css`
  padding-left: ${styleConstants.widths.headerPaddingXLGUp};
  padding-right: ${styleConstants.widths.headerPaddingXLGUp};
  ${getBreakpointOnly(breakpointSizes.LG)(css`
    padding-left: ${styleConstants.widths.headerPaddingLGOnly};
    padding-right: ${styleConstants.widths.headerPaddingLGOnly};
  `)}
  ${getBreakpointOnly(breakpointSizes.MD)(css`
    padding-left: ${styleConstants.widths.headerPaddingMDOnly};
    padding-right: ${styleConstants.widths.headerPaddingMDOnly};
  `)}
  ${getBreakpointOnly(breakpointSizes.SM)(css`
    padding-left: ${styleConstants.widths.headerPaddingSMOnly};
    padding-right: ${styleConstants.widths.headerPaddingSMOnly};
  `)}
  ${getBreakpointOnly(breakpointSizes.XSM)(css`
    padding-left: ${styleConstants.widths.headerPaddingXSMOnly};
    padding-right: ${styleConstants.widths.headerPaddingXSMOnly};
  `)}
  ${getBreakpointOnly(breakpointSizes.XXSM)(css`
    padding-left: ${styleConstants.widths.headerPaddingXXSMOnly};
    padding-right: ${styleConstants.widths.headerPaddingXXSMOnly};
  `)}
`

/**
 * The fixed, opaque compact bar. `position: fixed` (never sticky — a sticky
 * child un-sticks the moment the short `<Header>` scrolls past).
 *
 * LEFT (fix, avoids sidenav occlusion): anchored `right: 0` and shrunk by the
 * sidenav's reserved width via `sidebarRightShift`, exactly as `TopBar` /
 * `LayoutShell` do — never `left: 0`, which would hide the avatar + name behind
 * the fixed sidenav. Full-width only below XSM, where there is no reserved rail.
 *
 * TOP: `fixedElementPushdown` + the global header's height, so a runtime banner
 * shifts the bar and the swap threshold together. At SM-up the header is the
 * real 72px `TopBar` (`GLOBAL_HEADER_HEIGHT_PX`), and the bar's box is grown so
 * its BOTTOM stays at `topNavSMUp + CONDENSED_HERO_HEIGHT` — the Subnav's sticky
 * top, unchanged — leaving no gap under the header (the 8px the removed `Topnav`
 * used to cover) nor above the tabs. The content is vertically centred, so the
 * bar still reads as the 52px design height. The `top` half is a MEDIA QUERY,
 * not a JS value, and must stay one: it is interpolated at render and nothing
 * re-renders it on resize (the swap hook's `measure()` only sets state on an
 * open/closed change), so a JS value would go stale across the breakpoint;
 * branching in CSS keeps it in step with the header for free.
 *
 * XSM (mobile) is left on the pre-existing `topNavXSMDown` (58px) model. The
 * 58-vs-72 discrepancy predates this work and its correct value depends on the
 * mobile header/layout, which is untested here — deliberately not touched.
 *
 * When closed it stays mounted but inert + visually hidden (the swap partner
 * keeps a valid, non-duplicating DOM), fading only where motion is allowed.
 */
export const bar = (
  fixedElementPushdown: number,
  open: boolean,
  sideNavLockedOpen: boolean,
): CSSProp => css`
  position: fixed;
  right: 0;
  width: calc(100% - ${sidebarRightShift(sideNavLockedOpen)});
  top: ${fixedElementPushdown + GLOBAL_HEADER_HEIGHT_PX}px;
  /* Grow the box up to the real header while keeping the bottom at the Subnav's
     sticky top (topNavSMUp + condensed height); centred content reads as 52px. */
  height: ${px(styleConstants.heights.topNavSMUp) +
  CONDENSED_HERO_HEIGHT -
  GLOBAL_HEADER_HEIGHT_PX}px;
  ${getBreakpointDown(breakpointSizes.XSM)(css`
    left: 0;
    width: 100%;
    top: ${fixedElementPushdown + px(styleConstants.heights.topNavXSMDown)}px;
    height: ${CONDENSED_HERO_HEIGHT}px;
  `)}
  box-sizing: border-box;
  z-index: 3; /* above the sticky Subnav (z-index 2); below app modals */
  display: flex;
  align-items: center;
  gap: ${spacing(4)};
  background-color: ${styleConstants.colors.white};
  /* Hairline only — no drop shadow. The design separates this bar from the
     content below with a single 1px rule (and the Subnav beneath it does the
     same), so a shadow here reads as a second, heavier divider stacked under the
     first. */
  border-bottom: 1px solid ${base_colorContentBorder};
  ${horizontalPadding()};

  @media (prefers-reduced-motion: no-preference) {
    transition:
      opacity 150ms ease,
      visibility 150ms ease;
  }

  ${!open &&
  css`
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  `}
`

/** Avatar · name · ·· last4 · pill. Allowed to shrink/truncate before the actions. */
export const identity = css`
  display: flex;
  align-items: center;
  gap: ${spacing(2.5)};
  min-width: 0;
`

export const cardholderName = css`
  font-family: ${font_family_weight_semibold};
  ${font_md};
  color: ${text_colorTextPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const last4 = css`
  ${font_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
  white-space: nowrap;
`

/** A thin vertical rule between the identity cluster and the available-credit block. */
export const divider = css`
  flex-shrink: 0;
  width: 1px;
  height: ${spacing(6)};
  background-color: ${base_colorContentBorder};
`

/** "Available $X of $Y", single line. Collapses first when space is tight. */
export const available = css`
  display: inline-flex;
  align-items: baseline;
  gap: ${spacing(1.5)};
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
`

export const availableLabel = css`
  ${font_sm};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
`

export const availableAmount = css`
  ${font_md};
  font-family: ${font_family_weight_semibold};
  color: ${text_colorTextPrimary};
  font-variant-numeric: tabular-nums;
`

export const availableLimit = css`
  ${font_sm};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
`

/**
 * The reused Show details / Freeze / ••• cluster, pushed to the right edge. It is
 * the positioned ancestor for the reveal panel and the actions menu, which both
 * anchor absolutely below it.
 */
export const actions = css`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${spacing(2)};
  margin-left: auto;
  flex-shrink: 0;
`
